import { useEffect, useState, type ReactNode, type FormEvent } from 'react'

interface Props {
  children: ReactNode
}

type Status = 'checking' | 'locked' | 'unlocked' | 'submitting'

export function PreviewPasswordGate({ children }: Props) {
  const [status, setStatus] = useState<Status>('checking')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/preview-auth?action=check', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return
        setStatus(data?.unlocked ? 'unlocked' : 'locked')
      })
      .catch(() => {
        if (!cancelled) setStatus('locked')
      })
    return () => { cancelled = true }
  }, [])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setStatus('submitting')
    setError(null)
    try {
      const res = await fetch('/api/preview-auth?action=login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      })
      if (res.ok) {
        setStatus('unlocked')
        setPassword('')
        return
      }
      setError(res.status === 401 ? 'Incorrect password' : 'Something went wrong. Try again.')
      setStatus('locked')
    } catch {
      setError('Network error. Try again.')
      setStatus('locked')
    }
  }

  if (status === 'unlocked') return <>{children}</>

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm bg-surface rounded-2xl shadow-lg p-8 space-y-5">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Bloodstream Preview</h1>
          <p className="mt-1 text-sm text-slate-500">Enter the access password to continue.</p>
        </div>
        <input
          type="password"
          autoFocus
          autoComplete="current-password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Password"
          disabled={status === 'submitting'}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
        />
        {error && <div className="text-sm text-red-600">{error}</div>}
        <button
          type="submit"
          disabled={status === 'submitting' || !password}
          className="w-full rounded-lg bg-indigo-600 text-white text-sm font-medium px-3 py-2 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {status === 'submitting' ? 'Checking…' : 'Continue'}
        </button>
      </form>
    </div>
  )
}
